import FileUpload from "components/FileUpload";
import DynamicFormInput from "components/form/form";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateInternalLegalResearchRequest, updateLegalResearchRequest } from "services/legalResearchService";
import { LoadingIcon } from "utils/constant";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";

interface managementFeedbackProps {
    formState: any,
    setFormState: any,
    setIsRespondBackFormOpen: any,
    status: string,
    setIsFeedBackFormOpen?: any,
}

export default function ManagementFeedback({ setIsFeedBackFormOpen, formState, setFormState, setIsRespondBackFormOpen, status }: managementFeedbackProps) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [proposalFile, setProposalFile] = useState(null);
    const { showToast } = useToast();
    const contextData = useAuth();
    const [proposalDeadline, setProposalDeadline] = useState(
        new Date().toISOString().split("T")[0]
    );
    const handleFileChange = (
        file: any,
    ) => {
        setProposalFile(file);
    };
    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handlePresentationReject = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setLoading(true);
        const data = new FormData();

        data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.proposal_rejected.value);
        data.append("feedback_from_management", formState.feedback);
        if (proposalFile)
            data.append("feedback_from_management_file", proposalFile);

        try {
            let response: any;
            if (formState.requested_by === "internal")
                response = await updateInternalLegalResearchRequest(contextData.token, formState.id, data);
            else
                response = await updateLegalResearchRequest(formState.id, data, contextData.token);

            setFormState(response);
            showToast(t("ProposalRejectedSuccess"), "success");
            if (setIsFeedBackFormOpen) setIsFeedBackFormOpen(false);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateProposalFeedback"), "error");
        } finally {
            setLoading(false);
            setIsRespondBackFormOpen(false);
        }
    }

    const handlePresentationFeedBack = async () => {
        setLoading(true);
        const data = new FormData();

        if (status === LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value) {
            data.append("title_presentation_feedback", formState.feedback);
            data.append("proposal_deadline", proposalDeadline);
        }
        else
            data.append("feedback_from_management", formState.feedback);
        if (proposalFile)
            data.append("feedback_from_management_file", proposalFile);
        data.append("status", status);

        try {
            let response: any;
            if (formState.requested_by === "internal")
                response = await updateInternalLegalResearchRequest(contextData.token, formState.id, data);
            else
                response = await updateLegalResearchRequest(formState.id, data, contextData.token);

            setFormState((prev: any) => ({
                ...prev, ...response,
                urgency_level: formState?.urgency_level
                    ? formState?.urgency_level.charAt(0).toUpperCase() +
                    formState?.urgency_level.slice(1).toLowerCase()
                    : "High",
            }));
            showToast(t("ProposalFeedbackSubmittedSuccess"), "success");
            if (setIsFeedBackFormOpen) setIsFeedBackFormOpen(false);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateProposalFeedback"), "error");
        } finally {
            setLoading(false);
        }
    }

    return (
        <form
            onSubmit={handlePresentationReject}
        >
            <>
                {/* Commented out section - can be uncommented and translated later if needed */}
                <div className="mt-4">
                    <div className="mt-4">
                        <label
                            htmlFor="remark"
                            className="block text-sm font-medium text-gray-500"
                        >
                            {status === LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value ? t("Remark") : t("FeedbackFromManagement")}
                        </label>
                        <textarea
                            id="feedback"
                            name="feedback"
                            className="mt-2 outline-none bg-gray-50 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-24 resize-y"
                            placeholder={t("DetailHere")}
                            required
                            value={formState.feedback}
                            onChange={handleChange}
                        ></textarea>
                        <label
                            htmlFor="remark"
                            className="block text-sm font-medium text-gray-500 mt-4"
                        >
                            {status === LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value ? t("RemarkFile") : t("FeedbackFile")}
                        </label>
                        <div className="relative">
                            <FileUpload onChange={handleFileChange} />
                        </div>
                    </div>
                </div>
                <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                    {status !== LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value &&
                        <button
                            type="submit"
                            className="flex-1 px-6 py-3 rounded-md bg-[#E90102] text-white font-semibold hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    {LoadingIcon} {t("Submitting")}
                                </div>
                            ) : (
                                t("Reject")
                            )}
                        </button>
                    }
                    <div
                        onClick={handlePresentationFeedBack}
                        className="flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#212121] text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600"
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                {LoadingIcon} {t("Submitting")}
                            </div>
                        ) : (
                            t("SubmitFeedback")
                        )}
                    </div>
                </div>
            </>
        </form>
    );
}